import type { Box, Point } from './types'

export function boxFromPoints(points: Point[]): Box {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 }
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const p of points) {
    if (p.x < minX) minX = p.x
    if (p.y < minY) minY = p.y
    if (p.x > maxX) maxX = p.x
    if (p.y > maxY) maxY = p.y
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

export function boxContains(outer: Box, inner: Box): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  )
}

export function boxIntersects(a: Box, b: Box): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  )
}

export function expandBox(box: Box, amount: number): Box {
  return {
    x: box.x - amount,
    y: box.y - amount,
    width: box.width + amount * 2,
    height: box.height + amount * 2,
  }
}

export function boxCenter(box: Box): Point {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 }
}

export function commonBox(boxes: Box[]): Box {
  if (boxes.length === 0) return { x: 0, y: 0, width: 0, height: 0 }
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const b of boxes) {
    if (b.x < minX) minX = b.x
    if (b.y < minY) minY = b.y
    if (b.x + b.width > maxX) maxX = b.x + b.width
    if (b.y + b.height > maxY) maxY = b.y + b.height
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

// Page coordinates → screen coordinates
export function pageToScreen(
  point: Point,
  camera: { x: number; y: number; zoom: number },
): Point {
  return {
    x: (point.x + camera.x) * camera.zoom,
    y: (point.y + camera.y) * camera.zoom,
  }
}

// Screen coordinates → page coordinates
export function screenToPage(
  point: Point,
  camera: { x: number; y: number; zoom: number },
): Point {
  return {
    x: point.x / camera.zoom - camera.x,
    y: point.y / camera.zoom - camera.y,
  }
}

// Transform a point from page space into a shape's local space
export function pageToShape(
  shapeX: number,
  shapeY: number,
  point: Point,
): Point {
  return {
    x: point.x - shapeX,
    y: point.y - shapeY,
  }
}

// Transform a point from shape local space to page space
export function shapeToPage(
  shapeX: number,
  shapeY: number,
  point: Point,
): Point {
  return {
    x: point.x + shapeX,
    y: point.y + shapeY,
  }
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function clampPointToBox(point: Point, box: Box): Point {
  return {
    x: clamp(point.x, box.x, box.x + box.width),
    y: clamp(point.y, box.y, box.y + box.height),
  }
}

export function pointDistance(a: Point, b: Point): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}

// Multiply two points component-wise (Vec2d.MulV replacement)
export function mulVec(a: Point, b: Point): Point {
  return { x: a.x * b.x, y: a.y * b.y }
}
